import React from 'react';
import { string, object, oneOfType, number, func, bool } from 'prop-types';
import moment from 'moment';
import { get, size } from 'lodash';
import { Tooltip, Switch } from 'antd';
import { FlexContainer, Row, Text, StatusTag, StatInfo, IsMobileWrapper, If, HasPermissions } from '../../common';
import { drawRates } from '../../../utils/misc';
import MobileCard from './MobileCard';
import DesktopCard from './DesktopCard';

const DataBox = ({ label, value, size, align }) => (
    <FlexContainer direction='column' align={align}>
        <Row>
            <Text text={value} size={size} strong nowrap />
        </Row>
        <Row>
            <If
                condition={typeof label === 'string'}
                then={<Text text={label} size={12} semibold nowrap />}
                else={label || null}
            />
        </Row>
    </FlexContainer>
);

DataBox.propTypes = {
    label: string,
    value: oneOfType([string, number]),
    size: number,
    align: string
};

DataBox.defaultProps = {
    align: 'center'
};

const statusColors = {
    1: '#1FA02E',
    2: '#4DAEA9',
    3: '#89A503',
    4: '#666666'
};

const EmailWrapperComponent = ({ condition, label, children }) => {
    if (condition) {
        return (
            <div style={{ cursor: 'help' }}>
                <Tooltip title={label}>
                    { children }
                </Tooltip>
            </div>
        );
    };
    return children;
};

const ShopperCard = ({
    shortName,
    email,
    statusLabel,
    status,
    statistics,
    lastConnection,
    salesChannelActive,
    disconnectPicker,
    isModalActive,
    connections,
    company
}) => {
    const foundRate = get(statistics, 'foundrate', 0);
    const fillRate = get(statistics, 'fillrate', 0);
    const shopperInfo = [
        {
            key: 'name',
            width: 225,
            size: 16,
            value: shortName,
            render: (
                <DataBox
                    label={
                        <HasPermissions to={['pickers-monitor:email']}>
                            <EmailWrapperComponent label={email} condition={size(email) > 31}>
                                <FlexContainer maxWidth={195}>
                                    <Text text={email} truncate />
                                </FlexContainer>
                            </EmailWrapperComponent>
                        </HasPermissions>     
                    }
                    value={shortName}
                    size={16}
                    align='left'
                />
            )
        },
        {
            key: 'status',
            width: 122,
            render: (
                <StatusTag
                    status={statusLabel}
                    color='#FFFFFF'
                    backgroundColor={statusColors[status]}
                />
            )
        },
        {
            key: 'disconnect',
            width: 75,
            render: status === 4 ? (<Tooltip placement='top' title={'Activar/Desactivar shopper'}>
                <Row align='center' justify='center'>
                    <Switch
                        loading={isModalActive}
                        checked
                        onClick={disconnectPicker}
                    />
                </Row>
            </Tooltip>
            ) : () => {}
        },
        {
            key: 'foundrate',
            width: 100,
            render: (
                <StatInfo
                    value={foundRate}
                    color={drawRates(foundRate, 'color')}
                    icon={drawRates(foundRate, 'arrow')}
                    label='Foundrate'
                    size={20}
                    labelSize={12}
                    semibold
                />
            )
        },
        {
            key: 'fillrate',
            width: 100,
            render: (
                <StatInfo
                    value={fillRate}
                    color={drawRates(fillRate, 'color')}
                    icon={drawRates(fillRate, 'arrow')}
                    label='Fillrate'
                    size={20}
                    labelSize={12}
                    semibold
                />
            )
        },
        {
            key: 'contactability',
            width: 122,
            render: <DataBox label='Contactabilidad' value={`${get(statistics, 'contactEffectiveness', '0')}%`} size={20} />
        },
        {
            key: 'ordersCompleted',
            width: 140,
            render: <DataBox label='Órdenes pickeadas' value={`${get(statistics, 'ordersCompleted', '0')}`} size={20} />
        },
        {
            key: 'company',
            width: 162,
            render: <DataBox label='Empresa' value={company} size={16} />
        },
        {
            key: 'connections',
            width: 162,
            render: <DataBox label='Cantidad de conexiones' value={get(connections, 'count', '0')} size={16} />
        },
        {
            key: 'lastConnection',
            width: 162,
            render: <DataBox label='Última conexión' value={lastConnection && moment(lastConnection, 'YYYY-MM-DD HH:mm:ss').format('DD/MM/YYYY HH:mm') || 'No Disponible'} size={16} />
        },
        {
            key: 'salesChannelActive',
            width: 240,
            value: <DataBox label='Última Tienda' value={salesChannelActive} size={14} />
        }
    ];

    return (
        <IsMobileWrapper>
            {({ isMobile }) => (
                <If
                    condition={isMobile}
                    then={<MobileCard {...shopperInfo} />}
                    else={<DesktopCard {...shopperInfo} />}
                />
            )}
        </IsMobileWrapper>
    );
};

ShopperCard.propTypes = {
    shortName: string,
    statusLabel: string,
    statistics: object,
    email: string,
    lastConnection: string,
    status: number,
    salesChannelActive: string,
    disconnectPicker: func,
    isModalActive: bool
};

ShopperCard.defaultProps = {
    shortName: '',
    statusLabel: 'Inactivo',
    statistics: {}
};

export default ShopperCard;
